import { DateTime } from 'luxon';
import { z } from 'zod';
import { memberIdSchema } from '../identity/legacy-identity-id';
import { minorStatusSchema } from '../minors/age';
import {
  CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS,
  conversionAttributionSchema,
} from '../attribution/schema';
import { MEMBER_PROFILE_IDENTITY_TEXT_MAX } from '../member-profiles/schema';
import { SAML_SESSION_HANDLE_MAX_LENGTH } from './saml';
import { ELIGIBILITY_STAFF_REVIEW_REASON } from './review-reason';
import { safeSponsorRefSchema } from './sponsor-ref';

// Governed enums + wire contracts for the eligibility-verification domain.
//
// Each constrained string domain is a single source of truth (ADR-056 Rule 8):
// the `const X = [...] as const` array drives BOTH the `text`+CHECK constraint
// rendered in the DB schema and the wire/contract schema here, so the DB and
// the validators cannot drift. Mirrors the programs/schema.ts pattern.

// ─── eligibility_checks lifecycle ───────────────────────────────────────────
export const ELIGIBILITY_CHECK_STATUS = ['pending', 'matched', 'no_match', 'failed'] as const;
export const eligibilityCheckStatusSchema = z.enum(ELIGIBILITY_CHECK_STATUS);
export type EligibilityCheckStatus = z.infer<typeof eligibilityCheckStatusSchema>;

// Who wrote the row. `member_submission` is the member's own verification
// attempt. `legacy_import` is a 1.0 eligible verdict carried over at
// migration: matched on arrival, with no submitted identity, and never
// published as a resolution event. `migration_unlinked` is a terminal
// needs-review row for a member whose 1.0 source held no employer link.
export const ELIGIBILITY_CHECK_ORIGIN = [
  'member_submission',
  'legacy_import',
  'migration_unlinked',
] as const;
export const eligibilityCheckOriginSchema = z.enum(ELIGIBILITY_CHECK_ORIGIN);
export type EligibilityCheckOrigin = z.infer<typeof eligibilityCheckOriginSchema>;

// How a check row's match input is shaped: the member-submitted demographic
// identity, or the external roster id carried by a sealed SAML session
// . Drives the eligibility_checks.match_mode CHECK constraint.
export const ELIGIBILITY_CHECK_MATCH_MODE = ['demographic', 'external_id'] as const;
export const eligibilityCheckMatchModeSchema = z.enum(ELIGIBILITY_CHECK_MATCH_MODE);
export type EligibilityCheckMatchMode = z.infer<typeof eligibilityCheckMatchModeSchema>;

// Member-visible derived verification status. `needs_review` is deliberate —
// the automated check never hard-declares a member ineligible; unresolved
// outcomes route to the (future) manual-review path instead.
export const ELIGIBILITY_VIEW_STATUS = [
  'not_started',
  'pending',
  'eligible',
  'needs_review',
] as const;
export const eligibilityViewStatusSchema = z.enum(ELIGIBILITY_VIEW_STATUS);
export type EligibilityViewStatus = z.infer<typeof eligibilityViewStatusSchema>;

// ─── provider outcome (persisted into eligibility_checks.resolution) ───────
// Shape mirrors the Pangea eligibility-service MatchResult so the seam stays
// drop-in compatible with both candidate real providers (the internaltools
// checking layer and Pangea's deployed verify cascade).
export const ELIGIBILITY_MATCH_TIER = ['deterministic', 'fuzzy', 'llm', 'manual', 'none'] as const;
export const eligibilityMatchTierSchema = z.enum(ELIGIBILITY_MATCH_TIER);
export type EligibilityMatchTier = z.infer<typeof eligibilityMatchTierSchema>;

// Resolution outcomes and the closed provider-name vocabulary. Provider names
// double as the `ELIGIBILITY_PROVIDER` env-var values (ADR-053) and the
// chartable `provider` analytics dimension, so the set stays closed here.
export const ELIGIBILITY_OUTCOME = ['matched', 'no_match', 'failed'] as const;
export const eligibilityOutcomeSchema = z.enum(ELIGIBILITY_OUTCOME);
export type EligibilityOutcome = z.infer<typeof eligibilityOutcomeSchema>;

export const ELIGIBILITY_PROVIDER_NAMES = [
  'fake',
  'fake-pangea',
  'internaltools',
  'pangea',
] as const;
export const eligibilityProviderNameSchema = z.enum(ELIGIBILITY_PROVIDER_NAMES);
export type EligibilityProviderName = z.infer<typeof eligibilityProviderNameSchema>;

// Benefit-holder relationship disambiguator: whether the sponsor benefit is
// the member's own (`primary`) or shared with them (`dependent`). Drives both
// the eligibility_checks CHECK constraint and the wire schema.
export const ELIGIBILITY_CHECK_RELATIONSHIP = ['primary', 'dependent'] as const;
export const eligibilityCheckRelationshipSchema = z.enum(ELIGIBILITY_CHECK_RELATIONSHIP);
export type EligibilityCheckRelationship = z.infer<typeof eligibilityCheckRelationshipSchema>;

// A resolution recorded without any provider having run (e.g. the pointer
// event could not be published) carries provider `none`. A staff member's
// manual confirmation carries `manual`; it is a resolution provider only, never
// a selectable chain tier.
export const ELIGIBILITY_RESOLUTION_PROVIDER = [
  ...ELIGIBILITY_PROVIDER_NAMES,
  'none',
  'manual',
] as const;
export const eligibilityResolutionProviderSchema = z.enum(ELIGIBILITY_RESOLUTION_PROVIDER);
export type EligibilityResolutionProvider = z.infer<typeof eligibilityResolutionProviderSchema>;

// One attempted tier in a cascading resolution: lifecycle facts only. The
// strict object makes a submitted identity field unrepresentable in the
// persisted trail — an unknown key is a parse failure, never silently stored.
export const eligibilityCascadeEntrySchema = z
  .object({
    provider: eligibilityResolutionProviderSchema,
    outcome: eligibilityOutcomeSchema,
    reviewReason: z.string().nullable(),
  })
  .strict();
export type EligibilityCascadeEntry = z.infer<typeof eligibilityCascadeEntrySchema>;

// Roster demographics carried on a matched provider result so the client can
// prefill the registration profile screens. Every field is nullable — rosters
// vary in what they carry — and the whole block is PHI: it is returned only to
// the authenticated matched member on the eligible status view, and never
// enters logs, metric tags, analytics properties, or event payloads (ADR-069).
export const profileCandidateAddressSchema = z
  .object({
    line1: z.string().nullable(),
    line2: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
  })
  .strict();
export type ProfileCandidateAddress = z.infer<typeof profileCandidateAddressSchema>;

export const eligibilityProfileCandidateSchema = z
  .object({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    address: profileCandidateAddressSchema.nullable(),
    preferredLanguage: z.string().nullable().optional(),
    sexAssignedAtBirth: z.string().nullable(),
  })
  .strict();
export type EligibilityProfileCandidate = z.infer<typeof eligibilityProfileCandidateSchema>;

export const eligibilityResolutionSchema = z.object({
  outcome: eligibilityOutcomeSchema,
  provider: eligibilityResolutionProviderSchema,
  confidenceScore: z.number().min(0).max(1).nullable(),
  matchTier: eligibilityMatchTierSchema,
  matchedClientRef: z.string().nullable(),
  matchedMemberRef: z.string().nullable(),
  grantedProgramKeys: z.array(z.string()),
  reviewReason: z.string().nullable(),
  // Ordered per-tier attempt trail. Optional so resolutions persisted before
  // the cascade existed — and single-provider results — stay schema-valid.
  cascade: z.array(eligibilityCascadeEntrySchema).optional(),
  // The resolved internal ids exactly as published on the resolved event, so
  // a post-crash republish reads them from the row instead of re-resolving
  // reference data that may have changed since. Optional: resolutions
  // persisted before publish-marker tracking stay schema-valid.
  matchedClientId: z.string().uuid().nullable().optional(),
  grantedProgramDefinitionIds: z.array(z.string().uuid()).optional(),
  // Roster demographics from the matched record, persisted so the status view
  // can prefill without re-running the provider. Optional: pre-existing
  // resolutions and non-matched outcomes stay schema-valid.
  profileCandidate: eligibilityProfileCandidateSchema.nullable().optional(),
  // Member-level HDHP flag from the matched roster record, persisted for the
  // hdhp-notice derivation. Optional: pre-existing resolutions and
  // non-matched outcomes stay schema-valid; never copied onto
  // member_eligibilities.
  hdhpFlag: z.boolean().nullable().optional(),
});
export type EligibilityResolution = z.infer<typeof eligibilityResolutionSchema>;

// ─── wire contracts ─────────────────────────────────────────────────────────
// camelCase wire (ADR-066). `.strict()` so an unknown key — including any
// attempt to smuggle a member identifier — is a 400, never silently honoured;
// the member is always the verified JWT subject (ADR-028).
const isoCalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be an ISO calendar date (YYYY-MM-DD)')
  // Luxon parses non-leniently, so impossible dates like 2026-02-31 come back
  // invalid instead of rolling over (ADR-096 owns all date parsing).
  .refine(
    (value) => DateTime.fromISO(value, { zone: 'utc' }).isValid,
    'must be a real calendar date',
  )
  // "Today" is deliberately the UTC calendar day (named zone per ADR-096
  // Rule 7): a DOB has no member zone yet at registration, and a ±1-day
  // boundary skew is immaterial for a not-in-the-future sanity guard.
  .refine(
    (value) => value <= (DateTime.now().setZone('utc').toISODate() ?? value),
    'must not be in the future',
  );

/**
 * A date of birth from outside the member's own input (an employer roster),
 * accepted only when it is a real, past ISO calendar date; anything else is
 * treated as absent.
 */
export function validDateOfBirthOrNull(value: string | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  return isoCalendarDate.safeParse(value).success ? value : null;
}

export const demographicEligibilityCheckRequestSchema = z
  .object({
    firstName: z.string().trim().min(1).max(120).optional(),
    lastName: z.string().trim().min(1).max(120),
    dateOfBirth: isoCalendarDate,
    zip: z.string().trim().min(1).max(16).optional(),
    accessCode: z.string().trim().min(1).max(64).optional(),
    // Optional client-scoping hint (e.g. carried from a partner landing-page
    // slug on web, or a company-directory selection in the app's registration
    // flow); passed through to the provider to scope or disambiguate.
    clientRef: z.string().trim().min(1).max(120).optional(),
    // Benefit-holder disambiguator from the registration flow.
    relationship: eligibilityCheckRelationshipSchema.optional(),
    // Match-input email collected in the registration flow for accounts whose
    // OTP channel was phone. The verifier's server-side account-email
    // enrichment stays authoritative when the members row carries an email;
    // this value never writes back to the members row.
    email: z.string().trim().email().max(254).optional(),
  })
  .strict();
export type DemographicEligibilityCheckRequest = z.infer<
  typeof demographicEligibilityCheckRequestSchema
>;

/**
 * The SAML form of a check: the opaque sealed session minted by
 * `POST /api/v1/eligibility/saml/sessions`. The server unseals it into an
 * external-ID match input. The only client-submitted identity field is the
 * date of birth, and only when the roster row carries none: a roster DOB
 * always wins over a submitted one.
 */
export const samlEligibilityCheckRequestSchema = z
  .object({
    samlSession: z.string().min(1).max(SAML_SESSION_HANDLE_MAX_LENGTH),
    dateOfBirth: isoCalendarDate.optional(),
  })
  .strict();
export type SamlEligibilityCheckRequest = z.infer<typeof samlEligibilityCheckRequestSchema>;

export const eligibilityCheckRequestSchema = z.union([
  demographicEligibilityCheckRequestSchema,
  samlEligibilityCheckRequestSchema,
]);
export type EligibilityCheckRequest = z.infer<typeof eligibilityCheckRequestSchema>;

/** Narrow a check request to its SAML form. */
export function isSamlEligibilityCheckRequest(
  request: EligibilityCheckRequest,
): request is SamlEligibilityCheckRequest {
  return 'samlSession' in request;
}

export const eligibilityCheckResponseSchema = z.object({
  checkId: z.string().uuid(),
  // `pending` on the happy path. `needs_review` is the degraded-path truth:
  // when the requested-event publish exhausts its retries the row is resolved
  // failed/publish_failed in the SAME request, and the next status poll would
  // derive needs_review — the response must not claim a pending verification
  // that this code path just foreclosed.
  status: z.enum(['pending', 'needs_review']),
});
export type EligibilityCheckResponse = z.infer<typeof eligibilityCheckResponseSchema>;

export const eligibilityStatusViewSchema = z.object({
  status: eligibilityViewStatusSchema,
  checkId: z.string().uuid().optional(),
  resolvedAt: z.string().datetime().optional(),
  // Present exactly when status is `eligible` (full-prefill disclosure,
  // infosec-approved): the matched roster demographics for confirm-not-type
  // prefill. Never present on any other status — non-eligible responses stay
  // an enumeration-safe constant shape.
  profileCandidate: eligibilityProfileCandidateSchema.nullable().optional(),
  // Present only when status is `eligible`: the member's effective program
  // keys as of THIS read — granted keys intersected with the sponsor's live
  // company config, so a portal config edit reflects on the next poll.
  // Absent (never empty-but-meaningful) when the derivation cannot run;
  // clients treat absent/empty as "no restriction" and show the full set.
  programKeys: z.array(z.string()).optional(),
});
export type EligibilityStatusView = z.infer<typeof eligibilityStatusViewSchema>;

// What web signup can restore for a signed-in member who left before saving
// their details: the legal name and employer reference from their own latest
// eligibility check. PHI (the name): returned only to that member, never
// logged, tagged or sent to analytics. The date of birth is never part of it.
// Constant shape: a member with no check gets three nulls. Not `.strict()`, so
// a field added later never breaks an older client (ADR-135).
export const signupResumeViewSchema = z.object({
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  clientRef: z.string().nullable(),
});
export type SignupResumeView = z.infer<typeof signupResumeViewSchema>;

// ─── registration profile completion (wire) ─────────────────────────────────
// The demographic payload the member confirms at the end of the registration
// flow. Values are PHI at rest in member_profiles; the write is member-authed
// and idempotent. camelCase wire, `.strict()` per house convention.
export const SEX_ASSIGNED_AT_BIRTH = ['male', 'female'] as const;
export const sexAssignedAtBirthSchema = z.enum(SEX_ASSIGNED_AT_BIRTH);
export type SexAssignedAtBirth = z.infer<typeof sexAssignedAtBirthSchema>;

// Provenance of the confirmed demographic content: roster-prefilled and
// confirmed untouched, hand-entered by the member, or a mix.
export const MEMBER_DEMOGRAPHICS_SOURCE = ['roster_confirmed', 'member_entered', 'mixed'] as const;
export const memberDemographicsSourceSchema = z.enum(MEMBER_DEMOGRAPHICS_SOURCE);
export type MemberDemographicsSource = z.infer<typeof memberDemographicsSourceSchema>;

/**
 * The one E.164 rule a member phone is held to wherever Galapagos stores it.
 * Exported so every writer shares it: the registration write below, the legacy
 * import, and the staff contact correction. A second copy of this regex drifts,
 * and the member ends up holding a number shaped one way when they typed it at
 * registration and another when someone corrected it for them.
 */
export const e164PhoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{6,14}$/, 'must be an E.164 phone number');

export const registrationProfileAddressSchema = z
  .object({
    line1: z.string().trim().min(1).max(200),
    line2: z.string().trim().max(200).optional(),
    city: z.string().trim().min(1).max(120),
    state: z.string().trim().min(1).max(60),
    zip: z.string().trim().min(1).max(16),
  })
  .strict();
export type RegistrationProfileAddress = z.infer<typeof registrationProfileAddressSchema>;

// The bound on each insurance plan identifier, shared by the wire and the
// member_profiles CHECKs so the database never becomes the first rejection.
// Production's health-plan screen has no length or character rule; this is a
// generous ceiling that keeps the replicated text columns bounded.
export const REGISTRATION_PLAN_ID_MAX_LENGTH = 100;

const registrationPlanIdSchema = z.string().trim().min(1).max(REGISTRATION_PLAN_ID_MAX_LENGTH);

// A health-plan member's insurance identifiers, as printed on their card: the
// plan member (subscriber) ID, never the Pelago member id, and the optional
// group number. Names match the legacy record so imported and new members line
// up. Both values are PHI. The group number exists only inside this object, so
// a group number without a member ID cannot be expressed.
export const registrationPlanDetailsSchema = z
  .object({
    planMemberId: registrationPlanIdSchema,
    planGroupNo: registrationPlanIdSchema.optional(),
  })
  .strict();
export type RegistrationPlanDetails = z.infer<typeof registrationPlanDetailsSchema>;

const registrationIdentityTextSchema = z
  .string()
  .trim()
  .min(1)
  .max(MEMBER_PROFILE_IDENTITY_TEXT_MAX);

export const registrationProfileWriteSchema = z
  .object({
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().min(1).max(120),
    // The inverse-channel contact collected in the flow. At most one is newly
    // collected; both stay optional because the account's existing channel is
    // already verified on the members row.
    email: z.string().trim().email().max(254).optional(),
    phone: e164PhoneSchema.optional(),
    address: registrationProfileAddressSchema,
    sexAssignedAtBirth: sexAssignedAtBirthSchema,
    // The device's landing capture (ADR-132): the Iterable pair and/or the
    // acquisition values. Optional: an unattributed registration is the common
    // case. Validated here so a malformed record is a 400, never a silently
    // dropped analytics dimension.
    attribution: conversionAttributionSchema.optional(),
    // Collected only on the health-plan path; absent for everyone else.
    planDetails: registrationPlanDetailsSchema.optional(),
    // Optional identity details, in the member's own words or as one of the
    // preset keys in member-profiles/schema.ts. Absent when the member gave
    // none; never an empty string.
    genderIdentity: registrationIdentityTextSchema.optional(),
    pronouns: registrationIdentityTextSchema.optional(),
  })
  .strict();
export type RegistrationProfileWrite = z.infer<typeof registrationProfileWriteSchema>;

// The single server-derived branching decision for the registration flow:
// the client routes on this enum and never computes age or sees a DOB.
// `under_age`: below every program floor. `minor_unsupported`: a matched
// minor whose sponsoring client has minor support off (fail-closed capability
// accessor). Everything ambiguous — no match, unresolved client, disputed
// match — proceeds; the parental-consent hold downstream is the safety net.
export const REGISTRATION_GATE = ['proceed', 'under_age', 'minor_unsupported'] as const;
export const registrationGateSchema = z.enum(REGISTRATION_GATE);
export type RegistrationGate = z.infer<typeof registrationGateSchema>;

export const registrationProfileViewSchema = z.object({
  completedAt: z.string().datetime().nullable(),
  /**
   * Server-derived minor status (clients never compute age or see a DOB):
   * `unknown` without a usable DOB, `under_min_age` below every program
   * floor, `minor` below the state age of majority, else `adult`. Defaults
   * to `unknown` for tolerance against a server that predates the field.
   */
  minorStatus: minorStatusSchema.default('unknown'),
  /** See REGISTRATION_GATE above; defaults for tolerance against an old server. */
  registrationGate: registrationGateSchema.default('proceed'),
  /**
   * Catalog keys of programs whose floor exceeds the member's age — the
   * client subtracts these from onboarding options without knowing why.
   * Empty when age is unknown or no floor exceeds it.
   */
  ineligibleProgramKeys: z.array(z.string()).default([]),
  /** Server-authored programme rules for manual intake; absent on older servers. */
  manualIntakeProgramKeys: z.array(z.string()).optional(),
  /**
   * Whether the parental-consent hold applies right now: the member was
   * stamped a minor at enrolment and no granted parental consent row exists.
   * Server-derived, fail-closed; defaults false for old-server tolerance so
   * a stale server can never strand members on a nonexistent hold.
   */
  consentPending: z.boolean().default(false),
  /**
   * Whether the registration stage applies at all — false when the
   * `flag-registration-eligibility` kill switch is off, so the launch gate
   * routes past the flow without stamping completion. Defaults true for
   * tolerance against a server that predates the field.
   */
  required: z.boolean().default(true),
});
export type RegistrationProfileView = z.infer<typeof registrationProfileViewSchema>;

/**
 * The completion write's answer: the view, plus whether this save is the one
 * that completed registration. The server sets it from the same one-time
 * completion stamp that decides whether `member.profile.completed` publishes,
 * so a client can count a registration exactly once. Optional because a
 * newer client can meet a server that predates it; absent reads as "not this
 * save". Only the write carries it; the read never does.
 */
export const registrationProfileSaveViewSchema = registrationProfileViewSchema.extend({
  firstCompletion: z.boolean().optional(),
});
export type RegistrationProfileSaveView = z.infer<typeof registrationProfileSaveViewSchema>;

// ─── registration suggestion endpoints (wire) ───────────────────────────────
// Company-directory suggestion: `ref` is the opaque directory reference the
// client submits back as the check's `clientRef`. Decoy entries share this
// exact shape — nothing on the wire distinguishes them from real sponsors.
export const companySuggestionSchema = z
  .object({
    ref: z.string(),
    name: z.string(),
  })
  .strict();
export type CompanySuggestion = z.infer<typeof companySuggestionSchema>;

export const companySuggestionsViewSchema = z.object({
  suggestions: z.array(companySuggestionSchema),
});
export type CompanySuggestionsView = z.infer<typeof companySuggestionsViewSchema>;

// Address-suggestion candidate: `label` is the display line the member picks;
// the structured fields fill the address form on selection.
export const addressSuggestionSchema = z
  .object({
    label: z.string(),
    line1: z.string(),
    line2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  })
  .strict();
export type AddressSuggestion = z.infer<typeof addressSuggestionSchema>;

export const addressSuggestionsViewSchema = z.object({
  suggestions: z.array(addressSuggestionSchema),
});
export type AddressSuggestionsView = z.infer<typeof addressSuggestionsViewSchema>;

// Chartable prefill mix on the completion outcome — how much of the profile
// the roster candidate filled: everything (`full`), some fields (`partial`),
// or nothing (`none`, the fully manual path).
export const REGISTRATION_PREFILL = ['full', 'partial', 'none'] as const;
export const registrationPrefillSchema = z.enum(REGISTRATION_PREFILL);
export type RegistrationPrefill = z.infer<typeof registrationPrefillSchema>;

// ─── event payloads (ADR-052) ───────────────────────────────────────────────
// Strict pointer-shaped payloads: submitted identity fields live in the
// eligibility_checks row ONLY — the events table and CloudWatch sink stay
// PHI-free by construction. `.strict()` makes an identity-field leak a test
// failure, not a review catch.
export const eligibilityCheckRequestedPayloadSchema = z
  .object({
    checkId: z.string().uuid(),
    memberId: memberIdSchema,
    // Injected by buildAnnotatedDomainEvent from the analytics catalog —
    // closed to that exact value, never free text.
    status: z.literal('created').optional(),
  })
  .strict();
export type EligibilityCheckRequestedPayload = z.infer<
  typeof eligibilityCheckRequestedPayloadSchema
>;

export const memberEligibilityResolvedPayloadSchema = z
  .object({
    checkId: z.string().uuid(),
    memberId: memberIdSchema,
    outcome: eligibilityOutcomeSchema,
    provider: eligibilityResolutionProviderSchema,
    matchedClientId: z.string().uuid().nullable(),
    grantedProgramKeys: z.array(z.string()),
    // The already-resolved catalog ids for grantedProgramKeys (1:1 with the
    // persisted grant rows; empty unless matched) so consumers never
    // re-resolve keys against the catalog. Pointer values only (ADR-069):
    // never lifted into analytics.
    grantedProgramDefinitionIds: z.array(z.string().uuid()),
    // Injected by buildAnnotatedDomainEvent from the analytics catalog.
    status: z.literal('completed').optional(),
  })
  .strict();
export type MemberEligibilityResolvedPayload = z.infer<
  typeof memberEligibilityResolvedPayloadSchema
>;

/** The resolution outcomes that leave a member waiting on manual review (ADR-101 Rule 3). */
export const ELIGIBILITY_REVIEW_NEEDED_OUTCOMES = ['no_match', 'failed'] as const;

/**
 * Published beside `member.eligibility.resolved` when a check resolves to an
 * outcome that leaves the member waiting on manual review, so the ops alert has
 * its own Slack route (ADR-136 keys one route per event type, and the resolved
 * event's route is the onboarding card). Best-effort: the Atlas review queue
 * reads the check rows, so a lost publish costs only the nudge.
 *
 * Pointers only (ADR-069): no member id in `data` (the envelope's
 * `actor.memberId` carries it for suppression), the reason as a governed code
 * normalised at publish time, and the sponsor only when it is structurally a
 * short company number.
 */
export const memberEligibilityReviewNeededPayloadSchema = z
  .object({
    outcome: z.enum(ELIGIBILITY_REVIEW_NEEDED_OUTCOMES),
    reviewReason: z.enum(ELIGIBILITY_STAFF_REVIEW_REASON).nullable(),
    clientRef: safeSponsorRefSchema.nullable(),
  })
  .strict();
export type MemberEligibilityReviewNeededPayload = z.infer<
  typeof memberEligibilityReviewNeededPayloadSchema
>;

// Published when a verified match rewrites the legal name or date of birth an
// existing profile held. Pointers only: the corrected values are PHI and stay
// in member_profiles, so consumers re-read current state instead.
export const memberIdentityCorrectedPayloadSchema = z
  .object({
    checkId: z.string().uuid(),
    memberId: memberIdSchema,
  })
  .strict();
export type MemberIdentityCorrectedPayload = z.infer<typeof memberIdentityCorrectedPayloadSchema>;

// Published once per completed registration profile. Lifecycle facts only —
// the confirmed demographic values live in member_profiles (the address in
// member_addresses, ADR-130), never on the bus.
export const memberProfileCompletedPayloadSchema = z
  .object({
    memberId: memberIdSchema,
    prefill: registrationPrefillSchema,
    disambiguatorsUsed: z.number().int().min(0),
    // Minor-at-enrolment fact from the completion write — a derived boolean
    // only, never age/DOB/state (that PHI stays at rest in the database). Null means
    // no DOB was on file at completion: defensive, since the normal flow's
    // eligibility check requires a DOB and the mint path publishes no event.
    isMinor: z.boolean().nullable(),
    // Conversion attribution (ADR-132): the ten keys built from the record the
    // client attached to the completion write, or nulls when the registration
    // was not attributed. Always present so the forwarder never sees a gap.
    ...CONVERSION_ATTRIBUTION_EVENT_FIELD_SCHEMAS,
    // Injected by buildAnnotatedDomainEvent from the analytics catalog.
    status: z.literal('completed').optional(),
  })
  .strict();
export type MemberProfileCompletedPayload = z.infer<typeof memberProfileCompletedPayloadSchema>;
